import { PropsWithChildren } from 'react';
import { observer } from 'mobx-react-lite';
import { AnalyticsMetaData, getAnalyticTags } from '@lib-shared/lib/get-analytics-tags';
import { ButtonMetadata, ButtonSize, ButtonType } from 'components/buttons/button-types';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import styles from 'components/buttons/button.module.scss';

type ButtonProps = PropsWithChildren<any> & {
  meta: ButtonMetadata;
  isAnchor?: boolean;
  classes?: string[];
  onClick?: (e: MouseEvent) => void;
}

const formatValue = (value: string) => {
  value = (value ?? '').toLowerCase();
  value = toSnakeCase(value);
  value = replaceVariables(value);
  return value;
}

const toSnakeCase = (value: string) => value.replaceAll(' ', '_');

const replaceVariables = (value: string) => value.replace(/\[([^\]]+)\]/g, (_, key) => getProperty(key));

const getProperty = (key: string) => {
  const propertyKeys = Object.keys(AnalyticsStructureStore.properties);
  const match = propertyKeys.find(x => x.toLowerCase() === key.toLowerCase());
  return match ? AnalyticsStructureStore.properties[match] : `[${key}]`;
}

//Simplified version of the existing Button component that makes it easier to implement the required analytics
const Button = observer<ButtonProps>((props) => {
  const { children, classes = [], meta, isAnchor = false, onClick } = props;

  const componentAnalytics: AnalyticsMetaData = {
    component_name: formatValue(meta.componentName),
    component_type: isAnchor ? 'link' : 'button',
    cta_text: formatValue(meta.title),
    section_name: formatValue(meta.areaName),
  };

  const analyticsTags = getAnalyticTags(componentAnalytics);

  const handleClick = (e: any) => {
    if (meta?.disabled) {
      return;
    }

    if (onClick) {
      onClick(e);
    }
  }

  const cssClasses = isAnchor
    ? classes
    : [
      ...classes,
      styles.btn,
      'btn',
      meta?.type ? styles[meta?.type] : styles[ButtonType.Primary],
      meta?.size ? styles[meta?.size] : styles[ButtonSize.Medium],
      meta?.disabled && styles['btn-disabled'],
    ];

  const className = cssClasses.join(' ').trim();

  return (
    <button
      className={className}
      onClick={handleClick}
      disabled={meta?.disabled ?? false}
      type="button"
      {...analyticsTags}>
      {children || meta?.title}
    </button>
  );
});

export default Button;